import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Search } from 'lucide-react';
import { z } from 'zod';

import { DataTable, type DataColumn } from '@/components/data/DataTable';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardContent } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useApiError } from '@/lib/useApiError';
import { useCopyCatalog } from '@/queries/copy';
import {
  COPY_CATALOG_PAGE_SIZES,
  COPY_LOCALES,
  DEFAULT_COPY_CATALOG_PAGE_SIZE,
  copyFlagFilterSchema,
  copyStatusFilterSchema,
  localeStatus,
  type CopyCatalogFilters,
  type CopyCatalogPageSize,
  type CopyFlagFilter,
  type CopyInvalidRow,
  type CopyKey,
  type CopyStatusFilter,
} from '@/schemas/admin/copy';
import { CatalogEmptyState } from './CatalogEmptyState';
import { CopyEditorDrawer } from './CopyEditorDrawer';
import { CopyStatusChip } from './CopyStatusChip';
import { PublishBar } from './PublishBar';
import { SyncBanner } from './SyncBanner';

/**
 * The catalog's URL search. Every field falls back to "unset" rather than
 * throwing: a hand-edited or stale link (`?limit=30`, `?status=nope`) opens the
 * default view instead of the route's error screen.
 */
export const copyCatalogSearchSchema = z.object({
  q: z.string().max(200).optional().catch(undefined),
  namespace: z.string().max(80).optional().catch(undefined),
  surface: z.string().max(80).optional().catch(undefined),
  status: copyStatusFilterSchema.optional().catch(undefined),
  flag: copyFlagFilterSchema.optional().catch(undefined),
  limit: z.coerce
    .number()
    .refine((n): n is CopyCatalogPageSize =>
      (COPY_CATALOG_PAGE_SIZES as readonly number[]).includes(n)
    )
    .optional()
    .catch(undefined),
  selected: z.string().optional().catch(undefined),
});

export type CopyCatalogSearch = z.infer<typeof copyCatalogSearchSchema>;

const STATUS_TABS: readonly CopyStatusFilter[] = ['clean', 'draft', 'published'];
const FLAGS: readonly CopyFlagFilter[] = [
  'needs_eg_ar',
  'pending',
  'overridden',
  'baseline_changed',
  'orphaned',
];

export interface CopyCatalogScreenProps {
  search: CopyCatalogSearch;
  /** Apply a patch to the URL search; `undefined` or `''` removes a param. */
  onSearchChange: (patch: Partial<CopyCatalogSearch>) => void;
}

/**
 * The site-copy catalog. State lives in the URL (the route owns it), so a
 * filtered, paged view is shareable and survives a reload.
 */
export function CopyCatalogScreen({
  search,
  onSearchChange: setSearchPatch,
}: CopyCatalogScreenProps) {
  const { t } = useTranslation('copy');
  const queryClient = useQueryClient();
  const resolveError = useApiError();
  const pageSize = search.limit ?? DEFAULT_COPY_CATALOG_PAGE_SIZE;

  const filters: CopyCatalogFilters = {
    ...(search.q ? { q: search.q } : {}),
    ...(search.namespace ? { namespace: search.namespace } : {}),
    ...(search.surface ? { surface: search.surface } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.flag ? { flag: search.flag } : {}),
    limit: pageSize,
  };

  const list = useCopyCatalog(filters);
  const rows = React.useMemo(
    () => (list.data ? list.data.pages.flatMap((p) => p.items) : []),
    [list.data]
  );
  const invalidRows = React.useMemo(
    () => (list.data ? list.data.pages.flatMap((p) => p.invalid) : []),
    [list.data]
  );

  // Suggestions only — the filter itself is applied by the server, so a value
  // that has not appeared on a loaded page is still a valid thing to type.
  const namespaces = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.namespace))).sort(),
    [rows]
  );
  const surfaces = React.useMemo(
    () => Array.from(new Set(rows.map((r) => r.surface))).sort(),
    [rows]
  );

  const clearFilters = React.useCallback(
    () =>
      setSearchPatch({
        q: undefined,
        namespace: undefined,
        surface: undefined,
        status: undefined,
        flag: undefined,
        limit: undefined,
      }),
    [setSearchPatch]
  );

  // An unfiltered query that comes back empty means the catalog itself is
  // empty — nothing has been synced — which is a different problem, with a
  // different fix, from a filter that matches nothing.
  const filtered = [search.q, search.namespace, search.surface, search.status, search.flag].some(
    (v) => v !== undefined && v !== ''
  );
  const catalogEmpty =
    !filtered &&
    !list.isLoading &&
    !list.error &&
    !list.hasNextPage &&
    rows.length === 0 &&
    invalidRows.length === 0;

  // A new page size is a new query key, so the list starts again from its
  // first page. Once the screen has moved to it, drop what the other sizes had
  // loaded: going back to one must not resurrect a many-page list behind the
  // select. This runs after the switch, never before — removing a query that
  // is still observed makes its observer refetch the old size.
  const changePageSize = (raw: string) => {
    const next = COPY_CATALOG_PAGE_SIZES.find((n) => String(n) === raw);
    if (next === undefined || next === pageSize) return;
    setSearchPatch({ limit: next === DEFAULT_COPY_CATALOG_PAGE_SIZE ? undefined : next });
  };
  const previousPageSize = React.useRef(pageSize);
  React.useEffect(() => {
    if (previousPageSize.current === pageSize) return;
    previousPageSize.current = pageSize;
    queryClient.removeQueries({
      queryKey: ['copy', 'catalog'],
      predicate: (q) => {
        const params = q.queryKey[2] as { limit?: number } | string | undefined;
        return typeof params === 'object' && params.limit !== pageSize;
      },
    });
  }, [pageSize, queryClient]);

  // Typing must not fire a request per keystroke against a 624-row catalog.
  const [queryText, setQueryText] = React.useState(search.q ?? '');
  React.useEffect(() => {
    setQueryText(search.q ?? '');
  }, [search.q]);
  React.useEffect(() => {
    const current = search.q ?? '';
    if (queryText === current || (queryText.trim() === '' && current === '')) return;
    // Whitespace alone is not a search; the server folds the rest.
    const id = window.setTimeout(
      () => setSearchPatch({ q: queryText.trim() === '' ? undefined : queryText }),
      300
    );
    return () => window.clearTimeout(id);
  }, [queryText, search.q, setSearchPatch]);

  const columns: readonly DataColumn<CopyKey>[] = [
    {
      id: 'key',
      header: t('table.key'),
      cell: (r) => (
        <div className="flex min-w-0 flex-col">
          <span dir="ltr" className="font-mono text-xs font-semibold break-all">
            {r.key}
          </span>
          <span className="text-[11px] text-[color:var(--color-fg-muted)]">
            {t('meta.surface', { surface: r.surface })}
          </span>
        </div>
      ),
    },
    {
      id: 'shape',
      header: t('table.shape'),
      cell: (r) => <Badge tone="neutral">{t(`shape.${r.shape}`)}</Badge>,
    },
    {
      id: 'locales',
      header: t('table.locales'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {COPY_LOCALES.map((locale) => (
            <CopyStatusChip key={locale} locale={locale} status={localeStatus(r, locale)} />
          ))}
        </div>
      ),
    },
    {
      id: 'flags',
      header: t('table.flags'),
      cell: (r) => (
        <div className="flex flex-wrap gap-1">
          {r.needs_eg_ar ? <Badge tone="accent">{t('flags.needs_eg_ar')}</Badge> : null}
          {r.baseline_changed ? <Badge tone="info">{t('flags.baseline_changed')}</Badge> : null}
          {r.orphaned ? <Badge tone="warn">{t('flags.orphaned')}</Badge> : null}
          {!r.needs_eg_ar && !r.baseline_changed && !r.orphaned ? (
            <span className="text-xs text-[color:var(--color-fg-muted)]">—</span>
          ) : null}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold tracking-tight">{t('title')}</h1>
        <p className="max-w-3xl text-sm text-[color:var(--color-fg-muted)]">{t('subtitle')}</p>
      </header>

      <SyncBanner />

      {catalogEmpty ? (
        <CatalogEmptyState />
      ) : (
        <>
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div role="tablist" aria-label={t('filters.status')} className="flex flex-wrap gap-2">
                {[undefined, ...STATUS_TABS].map((s) => {
                  const selected = (search.status ?? undefined) === s;
                  return (
                    <button
                      key={s ?? 'all'}
                      role="tab"
                      type="button"
                      aria-selected={selected}
                      onClick={() => setSearchPatch({ status: s })}
                      className={
                        selected
                          ? 'rounded-[var(--radius-pill)] bg-[color:var(--color-accent)] px-3 py-1 text-xs font-semibold text-[color:var(--color-accent-fg)]'
                          : 'rounded-[var(--radius-pill)] bg-[color:var(--color-surface-2)] px-3 py-1 text-xs font-semibold text-[color:var(--color-fg-muted)]'
                      }
                    >
                      {s ? t(`status.${s}`) : t('filters.all')}
                    </button>
                  );
                })}
              </div>

              <div className="flex flex-wrap gap-2">
                {FLAGS.map((flag) => {
                  const selected = search.flag === flag;
                  return (
                    <button
                      key={flag}
                      type="button"
                      aria-pressed={selected}
                      onClick={() => setSearchPatch({ flag: selected ? undefined : flag })}
                      className={
                        selected
                          ? 'rounded-[var(--radius-pill)] border border-[color:var(--color-accent)] bg-[color:var(--color-accent-soft)] px-3 py-1 text-xs font-semibold text-[color:var(--color-accent)]'
                          : 'rounded-[var(--radius-pill)] border border-[color:var(--color-border)] px-3 py-1 text-xs font-semibold text-[color:var(--color-fg-muted)] hover:bg-[color:var(--color-surface-2)]'
                      }
                    >
                      {t(`flags.${flag}`)}
                    </button>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                <div className="lg:col-span-2">
                  <Label htmlFor="copy-q">{t('filters.search')}</Label>
                  <div className="relative">
                    <Search
                      aria-hidden
                      className="pointer-events-none absolute inset-y-0 start-3 my-auto size-4 text-[color:var(--color-fg-muted)]"
                    />
                    <Input
                      id="copy-q"
                      value={queryText}
                      onChange={(e) => setQueryText(e.target.value)}
                      placeholder={t('filters.searchPlaceholder')}
                      className="ps-9"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="copy-surface">{t('filters.surface')}</Label>
                  <Input
                    id="copy-surface"
                    list="copy-surface-options"
                    dir="ltr"
                    value={search.surface ?? ''}
                    onChange={(e) => setSearchPatch({ surface: e.target.value || undefined })}
                    placeholder={t('filters.all')}
                    className="font-mono"
                  />
                  <datalist id="copy-surface-options">
                    {surfaces.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                <div>
                  <Label htmlFor="copy-namespace">{t('filters.namespace')}</Label>
                  <Input
                    id="copy-namespace"
                    list="copy-namespace-options"
                    dir="ltr"
                    value={search.namespace ?? ''}
                    onChange={(e) => setSearchPatch({ namespace: e.target.value || undefined })}
                    placeholder={t('filters.all')}
                    className="font-mono"
                  />
                  <datalist id="copy-namespace-options">
                    {namespaces.map((n) => (
                      <option key={n} value={n} />
                    ))}
                  </datalist>
                </div>
              </div>

              <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                  <Label htmlFor="copy-limit">{t('filters.limit')}</Label>
                  <select
                    id="copy-limit"
                    dir="ltr"
                    value={String(pageSize)}
                    onChange={(e) => changePageSize(e.target.value)}
                    className="block h-11 rounded-[var(--radius-md)] border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 text-sm shadow-sm"
                  >
                    {COPY_CATALOG_PAGE_SIZES.map((n) => (
                      <option key={n} value={n}>
                        {n}
                      </option>
                    ))}
                  </select>
                </div>
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  {t('filters.clear')}
                </Button>
              </div>
            </CardContent>
          </Card>

          {invalidRows.length > 0 ? <InvalidRowsAlert rows={invalidRows} /> : null}

          {rows.length > 0 || invalidRows.length === 0 ? (
            <DataTable<CopyKey>
              data={rows}
              columns={columns}
              rowKey={(r) => r.key}
              loading={list.isLoading}
              error={list.error ? resolveError(list.error) : null}
              empty={{
                title: search.q ? t('emptySearch', { q: search.q.trim() }) : t('empty'),
                body: search.q ? t('emptySearchBody') : t('emptyBody'),
                action: (
                  <Button variant="outline" size="sm" onClick={clearFilters}>
                    {t('filters.clear')}
                  </Button>
                ),
              }}
              onRowClick={(r) => setSearchPatch({ selected: r.key })}
              caption={t('table.caption', { count: rows.length })}
            />
          ) : null}

          <div className="flex justify-center">
            {list.hasNextPage ? (
              <Button
                variant="outline"
                onClick={() => void list.fetchNextPage()}
                loading={list.isFetchingNextPage}
              >
                {t('table.loadMore')}
              </Button>
            ) : rows.length > 0 && !list.error ? (
              <p className="text-xs text-[color:var(--color-fg-muted)]">
                {t('table.allLoaded', { count: rows.length })}
              </p>
            ) : null}
          </div>
        </>
      )}

      <PublishBar onOpenKey={(key) => setSearchPatch({ selected: key })} />

      <CopyEditorDrawer
        copyKey={search.selected ?? null}
        open={Boolean(search.selected)}
        onOpenChange={(o) => {
          if (!o) setSearchPatch({ selected: undefined });
        }}
      />
    </div>
  );
}

/**
 * Rows the server sent that the console could not parse. The rest of the list
 * stays usable; these are named, with the exact schema paths, so the report to
 * engineering needs no screenshot.
 */
function InvalidRowsAlert({ rows }: { rows: readonly CopyInvalidRow[] }) {
  const { t } = useTranslation('copy');
  return (
    <Alert variant="warn" title={t('invalidRows.title', { count: rows.length })}>
      <p>{t('invalidRows.body')}</p>
      <ul dir="ltr" className="mt-2 space-y-2 text-start font-mono text-[11px]">
        {rows.map((row, i) => (
          <li key={row.key ?? `row-${i}`} className="break-all">
            <span className="font-semibold text-[color:var(--color-fg)]">{row.key ?? '—'}</span>
            {row.issues.map((issue) => (
              <span key={issue} className="block">
                {issue}
              </span>
            ))}
          </li>
        ))}
      </ul>
    </Alert>
  );
}
